import { AGENCY_ROLE, EMPLOYEE_ROLES } from '../constants/constants';
import { get, remove, set } from './localStorage';
import { isEmpty as isStringEmpty } from './string';

// Key for storing the intended destination URL
const REDIRECT_URL_KEY = "pendingRedirectUrl";

// Allowed external domains for redirects (security whitelist)
const ALLOWED_EXTERNAL_DOMAINS = [
  // Add any trusted external domains here if needed
  // Example: "trusted-domain.com"
];

/**
 * Validates if a URL is safe for redirection
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is safe for redirection
 */
export const isValidRedirectUrl = (url) => {
  if (isStringEmpty(url)) {
    return false;
  }

  try {
    const urlObj = new URL(url, window.location.origin);
    
    // Allow relative URLs and same-origin URLs
    if (urlObj.origin === window.location.origin) {
      return true;
    }

    // Check if external domain is in whitelist
    const hostname = urlObj.hostname.toLowerCase();
    return ALLOWED_EXTERNAL_DOMAINS.some(domain => 
      hostname === domain || hostname.endsWith(`.${domain}`)
    );
  } catch (error) {
    // Invalid URL format
    return false;
  }
};

/**
 * Stores the intended destination URL for post-authentication redirect
 * @param {string} url - The URL to redirect to after authentication
 * @param {Object} options - Additional options
 * @param {boolean} options.includeCurrentUrl - Whether to use current URL if no url provided
 */
export const storeRedirectUrl = (url, options = {}) => {
  const { includeCurrentUrl = true } = options;
  
  let urlToStore = url;
  
  // If no URL provided, use current location
  if (!urlToStore && includeCurrentUrl) {
    urlToStore = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  // Only store if it's a valid URL
  if (isValidRedirectUrl(urlToStore)) {
    set(REDIRECT_URL_KEY, urlToStore);
    return true;
  }
  
  return false;
};

/**
 * Retrieves the stored redirect URL
 * @param {boolean} clear - Whether to clear the stored URL after retrieval
 * @returns {string|null} - The stored URL or null if none exists
 */
export const getRedirectUrl = (clear = true) => {
  const storedUrl = get(REDIRECT_URL_KEY);
  
  if (clear && !isStringEmpty(storedUrl)) {
    remove(REDIRECT_URL_KEY);
  }
  
  return isStringEmpty(storedUrl) ? null : storedUrl;
};

/**
 * Clears any stored redirect URL
 */
export const clearRedirectUrl = () => {
  remove(REDIRECT_URL_KEY);
};

/**
 * Determines if a user has access to the recruit section
 *
 * @param {Object} user - User object with role and configuration
 *
 * @returns {boolean} - True if user has access to recruit section
 */
export const hasRecruitAccess = user => {
  if (!user) return false;

  // No access if the dashboard is not enabled
  if (user.configuration?.enable_recruit_dashboard !== true) {
    return false;
  }

  // Admin has access to everything
  if (user.role === EMPLOYEE_ROLES.admin.value) {
    return true;
  }

  // Agency role has access to retain
  if (user.role === AGENCY_ROLE.agency.value) {
    return true;
  }

  // Check if user has recruiter role
  if (user.role === EMPLOYEE_ROLES.recruiter.value) {
    return true;
  }

  return false;
};

/**
 * Determines if a user has access to the retain section
 * @param {Object} user - User object with role and configuration
 * @returns {boolean} - True if user has access to retain section
 */
export const hasRetainAccess = user => {
  if (!user) return false;

  // Admin has access to everything
  if (user.role === EMPLOYEE_ROLES.admin.value) {
    return true;
  }

  // Agency role has access to retain
  if (user.role === AGENCY_ROLE.agency.value) {
    return true;
  }

  // Caregiver role has access to retain
  if (user.role === EMPLOYEE_ROLES.caregiver.value) {
    return true;
  }

  return false;
};

/**
 * Gets the appropriate redirect URL after successful authentication
 * @param {string} userRole - The authenticated user's role
 * @param {number} agencyId - The user's agency ID
 * @param {Object} userConfiguration - User's configuration object
 * @returns {string} - The URL to redirect to
 */
export const getPostAuthRedirectUrl = (userRole, agencyId, userConfiguration = {}) => {
  // First, check if there's a stored redirect URL
  const storedUrl = getRedirectUrl(true);
  if (storedUrl) {
    return storedUrl;
  }

  // Create a user object for permission checking
  const user = {
    role: userRole,
    configuration: userConfiguration,
  };

  if (hasRecruitAccess(user)) {
    // If user only has recruit access, redirect to recruit
    if (!hasRetainAccess(user)) {
      return '/recruit/v1/candidates';
    }
  }

  if (hasRetainAccess(user)) {
    // Use agency-specific routing logic for retain
    if (agencyId === 36) {
      return '/retention/V2/inbox';
    }
    return '/retention/V3';
  }

  // Handle recognition module
  if (userRole === 'recognition') {
    return '/recognition/me';
  }

  // Default fallback
  return '/dashboard';
};

/**
 * Handles the complete post-authentication redirect
 * @param {Function} navigate - React Router navigate function
 * @param {string} userRole - The authenticated user's role
 * @param {number} agencyId - The user's agency ID
 * @param {Object} options - Additional options
 * @param {Object} userConfiguration - User's configuration object
 * @param {boolean} options.replace - Whether to use replace instead of push
 */
export const handlePostAuthRedirect = (
  navigate,
  userRole,
  agencyId,
  options = {},
  userConfiguration = {}
) => {
  const { replace = false } = options;
  const redirectUrl = getPostAuthRedirectUrl(userRole, agencyId, userConfiguration);

  if (replace) {
    navigate(redirectUrl, { replace: true });
  } else {
    navigate(redirectUrl);
  }
};

/**
 * Extracts and stores redirect URL from location state or current URL
 * @param {Object} location - React Router location object
 * @returns {boolean} - True if a redirect URL was stored
 */
export const captureRedirectUrl = (location) => {
  // Priority 1: Check location state for 'from' parameter
  if (location?.state?.from) {
    return storeRedirectUrl(location.state.from);
  }

  // Priority 2: Use current pathname if it's not a login/auth page
  const currentPath = location?.pathname || window.location.pathname;
  const authPaths = ["/", "/employee-login", "/agency-login", "/employee-signup"];
  
  if (!authPaths.includes(currentPath)) {
    return storeRedirectUrl(currentPath);
  }

  return false;
};
